import { inject, Injectable } from '@angular/core';
import { RegulationStatus } from '@vendasta/protos/sms/v2/enums';
import { getRegulationInfo$ } from '@vendasta/protos/sms/v2/regulation-service';
import type { GetRegulationInfoResponse, RegulationInfo } from '@vendasta/protos/sms/v2/types';
import { BehaviorSubject, catchError, combineLatest, distinctUntilChanged, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { SmsInjectionToken } from '../../token';
import { RegistrationStage } from '../a2p-registration/a2p-registration.constants';
import { getRegistrationStageFromRegulationStatus } from './regulation-helper-functions';

@Injectable({
  providedIn: 'root',
})
export class RegulationService {
  private readonly config = inject(SmsInjectionToken);

  private readonly refresh$ = new BehaviorSubject<number>(0);

  private readonly ownerId$ = this.config.ownerId$.pipe(distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }));

  public readonly regulationInfo$: Observable<RegulationInfo | null> = combineLatest([this.ownerId$, this.refresh$]).pipe(
    switchMap(([ownerId]) => {
      if (!ownerId) {
        return of(null);
      }
      return getRegulationInfo$({ ownerId }).pipe(
        map((response: GetRegulationInfoResponse) => response.regulationInfo ?? null),
        catchError((err) => {
          console.error('getRegulationInfo error:', err);
          return of(null);
        }),
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public readonly regulationStatus$: Observable<RegulationStatus> = this.regulationInfo$.pipe(
    map((info: RegulationInfo | null) => info?.status ?? RegulationStatus.REGULATION_STATUS_UNSPECIFIED),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public readonly hasSubmission$: Observable<boolean> = this.regulationInfo$.pipe(
    map(
      (info: RegulationInfo | null) =>
        info !== null && info.status !== RegulationStatus.REGULATION_STATUS_UNSPECIFIED,
    ),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public readonly registrationStage$: Observable<RegistrationStage> = this.regulationStatus$.pipe(
    map((status) => getRegistrationStageFromRegulationStatus(status)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  /** Re-fetch the latest regulation info for the current owner. Call after a successful form submission. */
  public refresh(): void {
    this.refresh$.next(this.refresh$.value + 1);
  }
}
